/*
 * TASKS.JSX - Tasks Component with Global User Permissions
 *
 *
 * SUMMARY:
 * Tasks component that renders project tasks, variations, and options with enhanced
 * permission controls allowing global users to maintain edit access during restricted
 * project states like quote processing.
 *
 */
import React from "react";
import { Typography, Collapse, Tooltip } from "@mui/material";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { numberFormat } from "../../util/currency-formatter";
import Icons from "../../components/icons/icons";
import deleteIcon from "../../assets/Dustbin.png";
import subtaskLine from "../../assets/subtaskLine.png";
import idx from "idx";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { CopyAllOutlined } from "@mui/icons-material";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import { getFormatedData } from "./task-list/task";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { roundValue } from "../../util/decimal-utils";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const getStoredFieldOpsExcluded = (item) =>
  item.excludeFromFieldOps ?? item.exclude_from_field_ops ?? false;

const getFieldOpsTooltip = (ownExcluded, isInheritedExcluded) => {
  if (isInheritedExcluded) return "Excluded from field ops via parent section";
  return ownExcluded ? "Excluded from field ops" : "Included in field ops";
};

function tasks({
  stages,
  options,
  stagesCost,
  taskCost,
  projectType,
  financePermission,
  updatePermission,
  writePermission, //included the global user permission
  quoteType,
  optionsTotal,
  toggleId,
  variations,
  handleTaskClick,
  deleteDialogHandler,
  optionDialogHandler,
  handleOptionMenu,
  stageId,
  switchSection,
  copyTasksToSection,
  stagesLength,
  isProjectVariation = false,
  isProjectOption = false,
  inProgressQuote = false,
  isGlobalUser = false,
  isSectionLocked,
  isFieldOpsInheritedExcluded = false,
  canInteractWithTaskFieldOps = false,
  onRequestTaskFieldOpsToggle,
  onRequestVariationFieldOpsToggle,
}) {
  let data = [];
  if (isProjectVariation && !stagesCost)
    data = idx(optionsTotal, (_) => _.projectTotal) || [];
  if (!isProjectVariation && !stagesCost)
    data = idx(optionsTotal, (_) => _.stageSummary[stageId]) || [];
  if (isProjectOption && !stagesCost && !isProjectVariation)
    data = idx(optionsTotal, (_) => _.stageSummary["projectlevel"]) || [];
  if (!isProjectOption && !stagesCost && !isProjectVariation)
    data = idx(optionsTotal, (_) => _.stageSummary[stageId]) || [];
  if (stagesCost && !isProjectVariation && !isProjectOption) data = stagesCost;

  const {
    materialSaleAmount,
    laborSaleAmount,
    subcontractorSaleAmount,
    totalSaleAmount,
    materialProfit,
    laborProfit,
    subcontractorProfit,
    totalProfit,
    materialProfitPercentage,
    laborProfitPercentage,
    subcontractorProfitPercentage,
    totalProfitPercentage,
    completionPercentage,
    forecastHoursDisplay,
    actualHoursDisplay,
    effortCompletionPercentage,
  } = getFormatedData(data || []);

  return (
    <>
      {Array.isArray(stages) &&
        stages?.map((stage, index) => {
          return (
            <div key={`${stage.id}-${index}`} className="table-body">
              <Droppable
                key={index}
                droppableId={`droppable${stage.id}`}
                type={`${index}`}
              >
                {(provided, snapshot) => (
                  <div
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={provided.innerRef}
                  >
                    {stage?.tasks?.map((stageTask, i) => {
                      const canManageTask =
                        !inProgressQuote || writePermission || isGlobalUser;
                      const canDeleteTask =
                        !inProgressQuote ||
                        (updatePermission &&
                          ((quoteType == "quote" &&
                            projectType == "TENDER") ||
                            quoteType == "noquote")) ||
                        isGlobalUser;
                      const canMoveTask =
                        (!inProgressQuote || updatePermission || isGlobalUser) &&
                        stagesLength > 1;
                      const showTaskFieldOpsIcon = !isSectionLocked;
                      const taskFieldOpsOwnExcluded =
                        getStoredFieldOpsExcluded(stageTask);
                      const taskFieldOpsExcluded =
                        isFieldOpsInheritedExcluded || taskFieldOpsOwnExcluded;
                      const canToggleTaskFieldOps =
                        canInteractWithTaskFieldOps &&
                        !isFieldOpsInheritedExcluded;
                      const taskFieldOpsTooltip = getFieldOpsTooltip(
                        taskFieldOpsOwnExcluded,
                        isFieldOpsInheritedExcluded,
                      );

                      return (
                        <Draggable
                          key={stageTask?.id}
                          draggableId={stageTask?.id}
                          index={i}
                        >
                          {(provided, snapshot) => (
                            <div
                              className="sub-task"
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              ref={provided.innerRef}
                            >
                              <ul
                                key={stageTask.id}
                                className="droppable tabel-list"
                              >
                                <li className="first-cell w-10">
                                  <span {...provided.dragHandleProps}>
                                    <Icons icon="dragHandle" />
                                  </span>

                                  <Typography
                                    onClick={() =>
                                      handleTaskClick({
                                        stageId: stage.id,
                                        taskId: stageTask.id,
                                        isSubTask: false,
                                        editTask: true,
                                      })
                                    }
                                    className="text-left text-truncate"
                                  >
                                    {stageTask?.name}
                                  </Typography>
                                </li>

                                <ProfitInPercentage
                                  stageTask={stageTask}
                                  taskCost={taskCost}
                                  projectType={projectType}
                                  financePermission={financePermission}
                                  isSectionLocked={isSectionLocked}
                                />
                                <li className="action-icons-task  gap-1 d-flex justify-content-center m-0 w-5">
                                  <div className={`d-flex ${!updatePermission ? "inner-action-icons-div" : "inner-action-icons"}`}>

                                    {canManageTask && (
                                      <Tooltip
                                        title="Edit Task"
                                        placement="left"
                                        arrow
                                        followCursor={true}
                                      >
                                        <ModeEditOutlineOutlinedIcon
                                          className="edit-icon"
                                          onClick={() =>
                                            handleTaskClick({
                                              stageId: stage.id,
                                              taskId: stageTask.id,
                                              editTask: true,
                                            })
                                          }
                                        />
                                      </Tooltip>
                                    )}
                                    {showTaskFieldOpsIcon && (
                                      <span
                                        className={`d-flex align-items-center ${canToggleTaskFieldOps
                                          ? "cursor-pointer"
                                          : ""
                                          }`}
                                        data-cy="task-field-ops-icon"
                                        onClick={
                                          canToggleTaskFieldOps
                                            ? (e) => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              if (!onRequestTaskFieldOpsToggle) return;
                                              onRequestTaskFieldOpsToggle(
                                                stageTask.id,
                                                !taskFieldOpsOwnExcluded,
                                                stageTask.name,
                                              );
                                            }
                                            : undefined
                                        }
                                      >
                                        <Tooltip
                                          title={taskFieldOpsTooltip}
                                          placement="top"
                                          arrow
                                        >
                                          <span className="d-flex align-items-center">
                                            {taskFieldOpsExcluded ? (
                                              <VisibilityOffIcon
                                                className="attribution-lock-icon"
                                                sx={{ color: "var(--brand-color-2)" }}
                                              />
                                            ) : (
                                              <VisibilityIcon
                                                className="attribution-lock-icon"
                                                sx={{ color: "var(--brand-color-2)" }}
                                              />
                                            )}
                                          </span>
                                        </Tooltip>
                                      </span>
                                    )}
                                    {canDeleteTask && (
                                      <Tooltip
                                        title="Delete Task"
                                        placement="top"
                                        arrow
                                        followCursor={true}
                                      >
                                        <DeleteOutlineOutlinedIcon
                                          className="delete-icon"
                                          onClick={() =>
                                            deleteDialogHandler(stageTask.id, "Task")
                                          }
                                        />
                                      </Tooltip>
                                    )}
                                    {canMoveTask && (
                                      <Tooltip
                                        title="Copy Task"
                                        placement="top"
                                        arrow
                                        followCursor={true}
                                      >
                                        <CopyAllOutlined
                                          className="copy-icon"
                                          onClick={() =>
                                            copyTasksToSection(
                                              stageTask.id,
                                              stage.id,
                                              "Task",
                                            )
                                          }
                                        />
                                      </Tooltip>
                                    )}
                                    {canMoveTask && (
                                      <Tooltip
                                        title="Change Section"
                                        placement="top"
                                        arrow
                                        followCursor={true}
                                      >
                                        <SwapHorizIcon
                                          className="swap-icon"
                                          onClick={() =>
                                            switchSection(
                                              stageTask.id,
                                              stage.id,
                                              "Task",
                                            )
                                          }
                                        />
                                      </Tooltip>
                                    )}
                                  </div>
                                </li>
                              </ul>

                              <ul className="collapsed-content tabel-lists">
                                <li
                                  colSpan={7}
                                  className={
                                    (
                                      "toggle" + stageTask.id === toggleId
                                        ? true
                                        : false
                                    )
                                      ? "collapsed"
                                      : "uncollapsed"
                                  }
                                >
                                  <Collapse
                                    in={
                                      "toggle" + stageTask.id === toggleId
                                        ? true
                                        : false
                                    }
                                    timeout="auto"
                                  >
                                    <Profit
                                      deleteDialogHandler={deleteDialogHandler}
                                      quoteType={quoteType}
                                      updatePermission={updatePermission}
                                      projectType={projectType}
                                      financePermission={financePermission}
                                      isSectionLocked={isSectionLocked} type={"Subtask"}
                                      stageTask={stageTask}
                                    />
                                  </Collapse>
                                </li>
                              </ul>
                              {provided.placeholder}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}

                    {financePermission && (
                      <ul className="stage-total-row tabel-list">
                        <li className="first-cell mt-0 w-10">
                          <Typography className="stageTotal" variant="body1">
                            Total
                          </Typography>
                        </li>

                        {/* TENDER: Planned Effort comes FIRST */}
                        {projectType === "TENDER" && financePermission && (
                          <li className="text-right mt-0 mb-auto w-150px d-flex flex-column justify-content-center" data-cy="planned-effort">
                            {stage.forecastHoursDisplay || "0 hrs"}
                          </li>
                        )}

                        {financePermission && (
                          <>
                            <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center" data-cy="material">
                              <Typography variant="body1" data-cy="sale-amount">{materialSaleAmount}</Typography>
                              <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                                {materialProfit} ({materialProfitPercentage})
                              </Typography>
                            </li>
                            <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center" data-cy="contractor">
                              <Typography variant="body1" data-cy="sale-amount">{subcontractorSaleAmount}</Typography>
                              <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                                {subcontractorProfit} ({subcontractorProfitPercentage})
                              </Typography>
                            </li>
                            <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center" data-cy="resource">
                              <Typography variant="body1" data-cy="sale-amount">{laborSaleAmount}</Typography>
                              <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                                {laborProfit} ({laborProfitPercentage})
                              </Typography>
                            </li>
                          </>
                        )}

                        {/* Non-TENDER: Effort comes AFTER Material/Contractor/Resource */}
                        {projectType != "TENDER" && (
                          <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center">
                            <Typography variant="body1" data-cy="stage-forecast-hours">{stage.forecastHoursDisplay || "0 hrs"}</Typography>
                            {!isSectionLocked ? (
                              <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                                {stage.actualHoursDisplay || "0 hrs"} (<span>{stage.effortCompletionPercentage}</span>)
                              </Typography>
                            ) : (
                              <Typography className="text-muted" variant="body1">-</Typography>
                            )}
                          </li>
                        )}

                        {projectType != "TENDER" && (
                          <li className="text-end mt-0 brand-color-1 w-150px d-flex flex-column justify-content-center">
                            {isSectionLocked ? <span className="text-muted">-</span> : completionPercentage}
                          </li>
                        )}

                        {financePermission && (
                          <li className="text-right mt-0 w-180px d-flex flex-column justify-content-center" data-cy="total">
                            <Typography variant="body1" data-cy="sale-amount">{totalSaleAmount}</Typography>
                            <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                              {totalProfit} ({totalProfitPercentage})
                            </Typography>
                          </li>
                        )}
                        <li className="action-icons-task pe-3 text-end w-5">
                          <Typography variant="body1"></Typography>
                        </li>
                      </ul>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}

      {!isProjectVariation &&
        Array.isArray(variations) &&
        (variations || []).some((variation) => variation.stageId === stageId) &&
        variations.length > 0 && (
          <>
            <div
              className="border-0 p-0 ps-2 table-top-row"
              data-cy={`stage-variations-header-${stageId}`}
            >
              <div className="col-12 col-sm-2">
                <h6 className="m-1">Variations</h6>
              </div>
            </div>
            {variations.map((variation, i) => {
              if (variation.stageId !== stageId) return null;
              const variationTotal =
                idx(optionsTotal, (_) => _.stageSummary[stageId].optionList) ||
                {};
              const canManageVariation =
                !inProgressQuote || writePermission || isGlobalUser;
              const canDeleteVariation =
                ((updatePermission &&
                  ((quoteType == "quote" && projectType == "TENDER") ||
                    quoteType == "noquote")) ||
                  isGlobalUser) &&
                canManageVariation;
              const showVariationFieldOpsIcon = !isSectionLocked;
              const variationOwnExcluded =
                getStoredFieldOpsExcluded(variation);
              const variationFieldOpsExcluded =
                isFieldOpsInheritedExcluded || variationOwnExcluded;
              const canToggleVariationFieldOps =
                (updatePermission || isGlobalUser) &&
                !isFieldOpsInheritedExcluded;
              const variationFieldOpsTooltip = getFieldOpsTooltip(
                variationOwnExcluded,
                isFieldOpsInheritedExcluded,
              );
              return (
                <div
                  key={variation.id}
                  data-cy={`stage-variation-${variation.id}`}
                >
                  <ul className="collapsed-content tabel-lists">
                    <li colSpan={7}>
                      <ul
                        className="tabel-list"
                        data-cy={`stage-variation-list-${variation.id}`}
                      >
                        <li
                          className="first-cell option_first_cell w-150px"
                          data-cy={`stage-variation-name-${variation.id}`}
                        >
                          <Typography
                            onClick={() =>
                              handleTaskClick({
                                isVariation: true,
                                optionId: variation.id,
                                isOption: true,
                                editOption: true,
                              })
                            }
                          >
                            <span>{`${variation.variationNo} ${variation.name}`}</span>
                          </Typography>
                        </li>

                        <ProfitInPercentage
                          stageTask={variation}
                          taskCost={variationTotal || {}}
                          projectType={projectType}
                          financePermission={financePermission}
                        />
                        <li className="action-icons-task pe-3 gap-1 d-flex justify-content-end m-0 w-5">
                          {canManageVariation && (
                            <Tooltip
                              title="Edit Variation"
                              placement="top"
                              arrow
                              followCursor={true}
                            >
                              <ModeEditOutlineOutlinedIcon
                                className="edit-icon"
                                onClick={() =>
                                  handleTaskClick({
                                    isVariation: true,
                                    optionId: variation.id,
                                    isOption: true,
                                    editOption: true,
                                    action: "view",
                                  })
                                }
                              />
                            </Tooltip>
                          )}
                          {canDeleteVariation && (
                            <Tooltip
                              title="Delete Variation"
                              placement="top"
                              arrow
                              followCursor={true}
                            >
                              <DeleteOutlineOutlinedIcon
                                className="delete-icon"
                                onClick={() =>
                                  deleteDialogHandler(
                                    variation.id,
                                    "VariationTask",
                                  )
                                }
                              />
                            </Tooltip>
                          )}
                          {showVariationFieldOpsIcon && (
                            <span
                              className={`d-flex align-items-center ${canToggleVariationFieldOps ? "cursor-pointer" : ""
                                }`}
                              onClick={
                                canToggleVariationFieldOps
                                  ? (e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    if (!onRequestVariationFieldOpsToggle) return;
                                    onRequestVariationFieldOpsToggle(
                                      variation.id,
                                      !variationOwnExcluded,
                                      `${variation.variationNo} ${variation.name}`,
                                    );
                                  }
                                  : undefined
                              }
                            >
                              <Tooltip
                                title={variationFieldOpsTooltip}
                                placement="top"
                                arrow
                              >
                                <span className="d-flex align-items-center">
                                  {variationFieldOpsExcluded ? (
                                    <VisibilityOffIcon
                                      className="attribution-lock-icon"
                                      sx={{ color: "var(--brand-color-2)" }}
                                    />
                                  ) : (
                                    <VisibilityIcon
                                      className="attribution-lock-icon"
                                      sx={{ color: "var(--brand-color-2)" }}
                                    />
                                  )}
                                </span>
                              </Tooltip>
                            </span>
                          )}
                          {/* {(updatePermission || isGlobalUser) &&
                            stagesLength > 1 && (
                              <Tooltip
                                title="Change Section"
                                placement="top"
                                arrow
                                followCursor={true}
                              >
                                <SwapHorizIcon
                                  className="swap-icon"
                                  onClick={() =>
                                    switchSection(
                                      variation.id,
                                      stageId,
                                      "Variation",
                                    )
                                  }
                                />
                              </Tooltip>
                            )} */}
                        </li>
                      </ul>
                    </li>
                  </ul>
                  <ul
                    className={
                      ("toggle" + variation.id == toggleId ? true : false)
                        ? "collapsed-content tabel-lists collapsedlist"
                        : "collapsed-content tabel-lists uncollapsed-list"
                    }
                  >
                    <li
                      className={
                        ("toggle" + variation.id == toggleId ? true : false)
                          ? "collapsed"
                          : "uncollapsed"
                      }
                    >
                      <Collapse
                        in={"toggle" + variation.id == toggleId ? true : false}
                        timeout="auto"
                      >
                        <Profit
                          deleteDialogHandler={deleteDialogHandler}
                          stageTask={variation}
                          type={"VariationSubtask"}
                          quoteType={quoteType}
                          updatePermission={updatePermission}
                          projectType={projectType}
                          financePermission={financePermission}
                          isSectionLocked={isSectionLocked}
                        />
                      </Collapse>
                    </li>
                  </ul>
                </div>
              );
            })}
            <ul className="stage-total-row tabel-list">
              <li className="first-cell w-10">
                <Typography className="stageTotal" variant="body1">
                  Total:
                </Typography>
              </li>
              {/* TENDER: Planned Effort comes FIRST */}
              {projectType === "TENDER" && financePermission && (
                <li className="text-right mt-0 mb-auto w-150px d-flex flex-column justify-content-center" data-cy="planned-effort">
                  {forecastHoursDisplay || "0 hrs"}
                </li>
              )}

              {financePermission && (
                <>
                  <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center" data-cy="material">
                    <Typography variant="body1" data-cy="sale-amount">{materialSaleAmount}</Typography>
                    <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                      {materialProfit} ({materialProfitPercentage})
                    </Typography>
                  </li>
                  <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center" data-cy="contractor">
                    <Typography variant="body1" data-cy="sale-amount">{subcontractorSaleAmount}</Typography>
                    <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                      {subcontractorProfit} ({subcontractorProfitPercentage})
                    </Typography>
                  </li>
                  <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center" data-cy="resource">
                    <Typography variant="body1" data-cy="sale-amount">{laborSaleAmount}</Typography>
                    <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                      {laborProfit} ({laborProfitPercentage})
                    </Typography>
                  </li>
                </>
              )}

              {/* Non-TENDER: Effort comes AFTER Material/Contractor/Resource */}
              {projectType !== "TENDER" && (
                <li className="text-end mt-0 w-150px d-flex flex-column justify-content-center">
                  <Typography variant="body2" className="fw-bold" data-cy="stage-variation-forecast-hours">
                    {forecastHoursDisplay || "0 hrs"}
                  </Typography>
                  {!isSectionLocked ? (
                    <Typography variant="caption" color="textSecondary" className="fst-12">
                      {actualHoursDisplay || "0 hrs"} ({effortCompletionPercentage})
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="textSecondary">-</Typography>
                  )}
                </li>
              )}

              {projectType !== "TENDER" && (
                <li className="text-end mt-0 brand-color-1 w-150px d-flex flex-column justify-content-center">
                  {isSectionLocked ? (
                    <Typography variant="body2" color="textSecondary">-</Typography>
                  ) : (
                    <Typography variant="body2" className="fw-bold">
                      {completionPercentage}
                    </Typography>
                  )}
                </li>
              )}

              {financePermission && (
                <li className="text-right mt-0 w-180px d-flex flex-column justify-content-center" data-cy="total">
                  <Typography variant="body1" className="option-top-text text-right" data-cy="sale-amount">{totalSaleAmount}</Typography>
                  <Typography className="fst-12 text-right" variant="subtitle1" color="textSecondary">
                    {totalProfit} ({totalProfitPercentage})
                  </Typography>
                </li>
              )}
              <li className="action-icons-task pe-3 text-end w-5">
                <Typography variant="body1"></Typography>
              </li>
            </ul>
          </>
        )}

      {/* Variations Section not Mapped */}

      {isProjectVariation &&
        projectType !== "TENDER" &&
        Array.isArray(variations) &&
        variations.map((variation, i) => {
          const canManageVariation =
            !inProgressQuote || writePermission || isGlobalUser;
          const canInteractWithVariationFieldOps =
            updatePermission || isGlobalUser;
          const variationFieldOpsExcluded =
            variation.excludeFromFieldOps ??
            variation.exclude_from_field_ops ??
            false;

          return (
            <div
              key={i}
              className="table-body"
              data-cy={`variation-row-${variation.id}`}
            >
              <Droppable
                key={i}
                droppableId={`droppable${variation.id}`}
                type={`${i}`}
              >
                {(provided, snapshot) => (
                  <div
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={provided.innerRef}
                  >
                    <Draggable
                      key={variation.id}
                      draggableId={variation.id}
                      index={i}
                      isDragDisabled={true}
                    >
                      {(provided, snapshot) => (
                        <div
                          className="sub-task"
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                          data-cy={`variation-item-${variation.id}`}
                        >
                          <ul
                            key={variation.id}
                            className="droppable tabel-list"
                            data-cy={`variation-list-${variation.id}`}
                          >
                            <li
                              className="first-cell w-10"
                              data-cy={`variation-name-${variation.id}`}
                            >
                              {/* <span {...provided.dragHandleProps}>
                                <Icons icon="dragHandle" />
                              </span> */}
                              <Typography
                                onClick={() =>
                                  handleTaskClick({
                                    isVariation: true,
                                    optionId: variation.id,
                                    isOption: true,
                                    editOption: true,
                                  })
                                }
                                className="text-left"
                              >
                                {`${variation.variationNo} ${variation.name}`}
                              </Typography>
                            </li>
                            <ProfitInPercentage
                              stageTask={variation}
                              taskCost={
                                idx(optionsTotal, (_) => _.optionList) || {}
                              }
                              projectType={projectType}
                              financePermission={financePermission}
                            />

                            <li className="action-icons-task pe-3 gap-1 d-flex justify-content-end m-0 w-5">
                              {canManageVariation && (
                                <>
                                  <Tooltip
                                    title="Edit Variation"
                                    placement="top"
                                    arrow
                                    followCursor={true}
                                  >
                                    <ModeEditOutlineOutlinedIcon
                                      className="edit-icon brand-color-3"
                                      onClick={() =>
                                        handleTaskClick({
                                          isVariation: true,
                                          optionId: variation.id,
                                          isOption: true,
                                          editOption: true,
                                        })
                                      }
                                    />
                                  </Tooltip>
                                </>
                              )}
                              <span
                                className={`d-flex align-items-center ${canInteractWithVariationFieldOps ? "cursor-pointer" : ""}`}
                                onClick={
                                  canInteractWithVariationFieldOps
                                    ? (e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      if (!onRequestVariationFieldOpsToggle) return;
                                      onRequestVariationFieldOpsToggle(
                                        variation.id,
                                        !variationFieldOpsExcluded,
                                        `${variation.variationNo} ${variation.name}`,
                                      );
                                    }
                                    : undefined
                                }
                              >
                                <Tooltip
                                  title={
                                    variationFieldOpsExcluded
                                      ? "Excluded from field ops"
                                      : "Included in field ops"
                                  }
                                  arrow
                                >
                                  {variationFieldOpsExcluded ? (
                                    <VisibilityOffIcon
                                      className="attribution-lock-icon"
                                      sx={{ color: "var(--brand-color-2)" }}
                                    />
                                  ) : (
                                    <VisibilityIcon
                                      className="attribution-lock-icon"
                                      sx={{ color: "var(--brand-color-2)" }}
                                    />
                                  )}
                                </Tooltip>
                              </span>
                              {/* {(updatePermission || isGlobalUser) && (
                                  <Tooltip
                                    title="Change Section"
                                    placement="top"
                                    arrow
                                    followCursor={true}
                                  >
                                    <SwapHorizIcon
                                      className="swap-icon"
                                      onClick={() =>
                                        switchSection(
                                          variation.id,
                                          stageId,
                                          "Variation",
                                        )
                                      }
                                    />
                                  </Tooltip>
                                )} */}
                            </li>
                          </ul>

                          <ul className="collapsed-content tabel-lists">
                            <li
                              colSpan={7}
                              className={
                                (
                                  "toggle" + variation.id === toggleId
                                    ? true
                                    : false
                                )
                                  ? "collapsed"
                                  : "uncollapsed"
                              }
                            >
                              <Collapse
                                in={
                                  "toggle" + variation.id === toggleId
                                    ? true
                                    : false
                                }
                                timeout="auto"
                              >
                                <Profit
                                  deleteDialogHandler={deleteDialogHandler}
                                  stageTask={variation}
                                  type={"VariationSubtask"}
                                  quoteType={quoteType}
                                  updatePermission={updatePermission}
                                  projectType={projectType}
                                  financePermission={financePermission}
                                  isSectionLocked={isSectionLocked}
                                />
                              </Collapse>
                            </li>
                          </ul>
                          {provided.placeholder}
                        </div>
                      )}
                    </Draggable>

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}

      {(isProjectVariation &&
        Array.isArray(variations) &&
        variations.length && (
          <ul className="stage-total-row tabel-list droppable">
            <li className="first-cell w-10">
              <Typography className="stageTotal" variant="body1">
                Total:
              </Typography>
            </li>

            {/* TENDER: Planned Effort comes FIRST */}
            {projectType === "TENDER" && financePermission && (
              <li className="text-right mt-0 mb-auto w-150px d-flex flex-column justify-content-center" data-cy="planned-effort">
                <Typography variant="body1" className="option-top-text">
                  {forecastHoursDisplay || "0 hrs"}
                </Typography>
              </li>
            )}

            {financePermission && (
              <>
                <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center" data-cy="material">
                  <Typography variant="body1" data-cy="sale-amount">{materialSaleAmount}</Typography>
                  <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                    {materialProfit} ({materialProfitPercentage})
                  </Typography>
                </li>
                <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center" data-cy="contractor">
                  <Typography variant="body1" data-cy="sale-amount">{subcontractorSaleAmount}</Typography>
                  <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                    {subcontractorProfit} ({subcontractorProfitPercentage})
                  </Typography>
                </li>
                <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center" data-cy="resource">
                  <Typography variant="body1" data-cy="sale-amount">{laborSaleAmount}</Typography>
                  <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                    {laborProfit} ({laborProfitPercentage})
                  </Typography>
                </li>
              </>
            )}

            {/* Non-TENDER: Effort comes AFTER Material/Contractor/Resource */}
            {projectType !== "TENDER" && (
              <li className="text-end mt-0 w-150px d-flex flex-column justify-content-center">
                <Typography variant="body1" data-cy="section-forecast-hours">
                  {forecastHoursDisplay || "0 hrs"}
                </Typography>
                {!isSectionLocked ? (
                  <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                    {actualHoursDisplay || "0 hrs"} (<span>{effortCompletionPercentage}</span>)
                  </Typography>
                ) : (
                  <Typography className="text-muted" variant="body1">-</Typography>
                )}
              </li>
            )}

            {projectType !== "TENDER" && (
              <li className="text-end mt-0 w-150px d-flex flex-column justify-content-center">
                {isSectionLocked ? <span className="text-muted">-</span> : completionPercentage}
              </li>
            )}

            {financePermission && (
              <li className="text-right mt-0 w-180px d-flex flex-column justify-content-center" data-cy="total">
                <Typography variant="body1" className="option-top-text text-right" data-cy="sale-amount">{totalSaleAmount}</Typography>
                <Typography className="fst-12 text-right" variant="subtitle1" color="textSecondary">
                  {totalProfit} ({totalProfitPercentage})
                </Typography>
              </li>
            )}
            <li className="w-5">
              <Typography variant="body1"></Typography>
            </li>
          </ul>
        )) ||
        ""}

      {!isProjectOption &&
        options &&
        options.length > 0 &&
        idx(optionsTotal, (_) => _.stageSummary[stageId]) && (
          <>
            <div class="border-0 p-0 ps-2 table-top-row">
              <div class="col-12 col-sm-2">
                <h6 class="m-1">Options</h6>
              </div>
            </div>
            {options.map((option, optionIndex) => {
              if (option.stageId !== stageId) return null;
              const optionTotal = optionsTotal || {};
              return (
                <div key={option.id}>
                  <ul className="collapsed-content tabel-lists">
                    <li colSpan={7}>
                      <ul className="tabel-list">
                        <li className="first-cell option_first_cell w-150px">
                          <Typography
                            onClick={() =>
                              optionDialogHandler({
                                optionId: option.id,
                                isOption: true,
                                editOption: true,
                              })
                            }
                          >
                            <span>{option.name}</span>
                            {option.revisionName && option.revisionNumber !== 1
                              ? " - " + option.revisionName
                              : ""}
                          </Typography>
                        </li>
                        <ProfitInPercentage
                          stageTask={option}
                          taskCost={optionTotal.optionSummary || {}}
                          projectType={projectType}
                          financePermission={financePermission}
                        />

                        <li className="action-icons-task pe-3 gap-1 d-flex justify-content-end m-0 ms-2 w-180px">
                          {quoteType !== "TENDER" && (
                            <span
                              onClick={(e) =>
                                handleOptionMenu(
                                  e,
                                  "OPTION",
                                  stageId,
                                  "",
                                  "",
                                  option.id,
                                )
                              }
                              className="menuIcon brand-color-4 cursor-pointer"
                            >
                              <MoreHorizOutlinedIcon />
                            </span>
                          )}

                          {((updatePermission &&
                            ((quoteType == "quote" &&
                              projectType == "TENDER") ||
                              quoteType == "noquote")) ||
                            isGlobalUser) && (
                              <Tooltip
                                title="Delete Option"
                                placement="top"
                                arrow
                                followCursor={true}
                              >
                                <DeleteOutlineOutlinedIcon
                                  className="delete-icon"
                                  onClick={() =>
                                    deleteDialogHandler(option.id, "OptionTask")
                                  }
                                />
                              </Tooltip>
                            )}
                          {/* {(updatePermission || isGlobalUser) &&
                            stagesLength > 1 && (
                              <Tooltip
                                title="Change Section"
                                placement="top"
                                arrow
                                followCursor={true}
                              >
                                <SwapHorizIcon
                                  className="swap-icon"
                                  onClick={() =>
                                    switchSection(option.id, stageId, "Option")
                                  }
                                />
                              </Tooltip>
                            )} */}
                        </li>
                      </ul>
                    </li>
                  </ul>
                  <ul
                    className={
                      ("toggle" + option.id == toggleId ? true : false)
                        ? "collapsed-content tabel-lists collapsedlist"
                        : "collapsed-content tabel-lists uncollapsed-list"
                    }
                  >
                    <li
                      className={
                        ("toggle" + option.id == toggleId ? true : false)
                          ? "collapsed"
                          : "uncollapsed"
                      }
                    >
                      <Collapse
                        in={"toggle" + option.id == toggleId ? true : false}
                        timeout="auto"
                      >
                        <Profit
                          deleteDialogHandler={deleteDialogHandler}
                          stageTask={option}
                          type={"OptionSubtask"}
                          quoteType={quoteType}
                          updatePermission={updatePermission}
                          projectType={projectType}
                          financePermission={financePermission}
                          isSectionLocked={isSectionLocked}
                        />
                      </Collapse>
                    </li>
                  </ul>
                </div>
              );
            })}

            <ul className="stage-total-row tabel-list">
              <li className="first-cell w-10">
                <Typography className="stageTotal" variant="body1">
                  Total:
                </Typography>
              </li>
              {projectType === "TENDER" && (
                <li className="text-right w-150px" data-cy="material">
                  <Typography
                    variant="body1"
                    className="option-top-text"
                    data-cy="sale-amount"
                  >
                    {numberFormat(
                      idx(
                        optionsTotal,
                        (_) => _.stageSummary[stageId].materialSale,
                      ) || 0,
                    )}
                  </Typography>
                  <Typography
                    className="fst-12"
                    variant="subtitle1"
                    color="textSecondary"
                  >
                    {numberFormat(
                      idx(
                        optionsTotal,
                        (_) => _.stageSummary[stageId].materialProfit,
                      ) || 0,
                    )}{" "}
                    (
                    {idx(
                      optionsTotal,
                      (_) => _.stageSummary[stageId].materialProfitPercentage,
                    ) || "0.00%"}
                    )
                  </Typography>
                </li>
              )}
              {projectType === "TENDER" && (
                <li className="text-right w-150px" data-cy="contractor">
                  <Typography
                    variant="body1"
                    className="option-top-text"
                    data-cy="sale-amount"
                  >
                    {numberFormat(
                      idx(
                        optionsTotal,
                        (_) => _.stageSummary[stageId].subcontractorSale,
                      ) || 0,
                    )}
                  </Typography>
                  <Typography
                    className="fst-12"
                    variant="subtitle1"
                    color="textSecondary"
                  >
                    {numberFormat(
                      idx(
                        optionsTotal,
                        (_) => _.stageSummary[stageId].subcontractorProfit,
                      ) || 0,
                    )}{" "}
                    (
                    {idx(
                      optionsTotal,
                      (_) =>
                        _.stageSummary[stageId].subcontractorProfitPercentage,
                    ) || "0.00%"}
                    )
                  </Typography>
                </li>
              )}
              {projectType === "TENDER" && (
                <li className="text-right w-150px" data-cy="resource">
                  <Typography
                    variant="body1"
                    className="option-top-text"
                    data-cy="sale-amount"
                  >
                    {numberFormat(
                      idx(
                        optionsTotal,
                        (_) => _.stageSummary[stageId].laborSale,
                      ) || 0,
                    )}
                  </Typography>
                  <Typography
                    className="fst-12"
                    variant="subtitle1"
                    color="textSecondary"
                  >
                    {numberFormat(
                      idx(
                        optionsTotal,
                        (_) => _.stageSummary[stageId].laborProfit,
                      ) || 0,
                    )}{" "}
                    (
                    {idx(
                      optionsTotal,
                      (_) => _.stageSummary[stageId].laborProfitPercentage,
                    ) || "0.00%"}
                    )
                  </Typography>
                </li>
              )}
              {projectType !== "TENDER" && (
                <li className="text-end mt-0 w-150px d-flex flex-column justify-content-center">
                  <Typography variant="body1" data-cy="stage-variation-forecast-hours">
                    {forecastHoursDisplay || "0 hrs"}
                  </Typography>
                  {!isSectionLocked ? (
                    <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                      {actualHoursDisplay || "0 hrs"} (<span>{effortCompletionPercentage}</span>)
                    </Typography>
                  ) : (
                    <Typography className="text-muted" variant="body1">-</Typography>
                  )}
                </li>
              )}

              {projectType !== "TENDER" && (
                <li className="text-end mt-0 w-150px d-flex flex-column justify-content-center">
                  {isSectionLocked ? <span className="text-muted">-</span> : completionPercentage}
                </li>
              )}

              <li className="text-end mt-0 w-10 d-flex flex-column justify-content-center" data-cy="total">
                <Typography variant="body1" className="option-top-text" data-cy="sale-amount">
                  {totalSaleAmount}
                </Typography>
                <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                  {totalProfit} ({totalProfitPercentage})
                </Typography>
              </li>
              <li className="action-icons-task pe-3 text-end ms-2 w-180px">
                <Typography variant="body1"></Typography>
              </li>
            </ul>
          </>
        )}

      {/* Option At Project Level */}

      {isProjectOption && options && options.length > 0 && (
        <>
          {options.map((option, optionIndex) => {
            if (option.stageId) return null;
            const optionTotal = optionsTotal || {};
            return (
              <div key={option.id}>
                <ul className="collapsed-content tabel-lists">
                  <li colSpan={7}>
                    <ul className="tabel-list">
                      <li className="first-cell option_first_cell w-150px">
                        <Typography
                          onClick={() =>
                            optionDialogHandler({
                              optionId: option.id,
                              isOption: true,
                              editOption: true,
                            })
                          }
                        >
                          <span>{option.name}</span>
                          {option.revisionName && option.revisionNumber !== 1
                            ? " - " + option.revisionName
                            : ""}
                        </Typography>
                      </li>
                      <ProfitInPercentage
                        stageTask={option}
                        taskCost={
                          idx(
                            optionTotal,
                            (_) => _.stageSummary.projectlevel.optionList,
                          ) || {}
                        }
                        projectType={projectType}
                        financePermission={financePermission}
                      />
                      <li className="action-icons-task pe-3 gap-1 d-flex justify-content-end m-0 ms-2 w-180px">
                        {quoteType !== "TENDER" && (
                          <span
                            onClick={(e) => {
                              handleOptionMenu(
                                e,
                                "OPTION",
                                stageId,
                                "",
                                "",
                                option.id,
                              );
                            }}
                            className="menuIcon brand-color-4 cursor-pointer"
                          >
                            <MoreHorizOutlinedIcon />
                          </span>
                        )}
                        {(updatePermission || isGlobalUser) && (
                          <Tooltip
                            title="Edit Option"
                            placement="top"
                            arrow
                            followCursor={true}
                          >
                            <ModeEditOutlineOutlinedIcon
                              className="edit-icon"
                              onClick={() =>
                                optionDialogHandler({
                                  optionId: option.id,
                                  isOption: true,
                                  editOption: true,
                                })
                              }
                            />
                          </Tooltip>
                        )}

                        {((updatePermission &&
                          ((quoteType == "quote" && projectType == "TENDER") ||
                            quoteType == "noquote")) ||
                          isGlobalUser) && (
                            <Tooltip
                              title="Delete Option"
                              placement="top"
                              arrow
                              followCursor={true}
                            >
                              <DeleteOutlineOutlinedIcon
                                className="delete-icon"
                                onClick={() =>
                                  deleteDialogHandler(option.id, "OptionTask")
                                }
                              />
                            </Tooltip>
                          )}
                        {(updatePermission || isGlobalUser) &&
                          stagesLength > 1 && (
                            <Tooltip
                              title="Change Section"
                              placement="top"
                              arrow
                              followCursor={true}
                            >
                              <SwapHorizIcon
                                className="swap-icon"
                                onClick={() =>
                                  switchSection(option.id, stageId, "Option")
                                }
                              />
                            </Tooltip>
                          )}
                      </li>
                    </ul>
                  </li>
                </ul>
                <ul
                  className={
                    ("toggle" + option.id == toggleId ? true : false)
                      ? "collapsed-content tabel-lists collapsedlist"
                      : "collapsed-content tabel-lists uncollapsed-list"
                  }
                >
                  <li
                    className={
                      ("toggle" + option.id == toggleId ? true : false)
                        ? "collapsed"
                        : "uncollapsed"
                    }
                  >
                    <Collapse
                      in={"toggle" + option.id == toggleId ? true : false}
                      timeout="auto"
                    >
                      <Profit
                        deleteDialogHandler={deleteDialogHandler}
                        stageTask={option}
                        type={"OptionSubtask"}
                        quoteType={quoteType}
                        updatePermission={updatePermission}
                        projectType={projectType}
                        financePermission={financePermission}
                        isSectionLocked={isSectionLocked}
                      />
                    </Collapse>
                  </li>
                </ul>
              </div>
            );
          })}

          <ul className="stage-total-row tabel-list">
            <li className="first-cell w-10">
              <Typography className="stageTotal" variant="body1">
                Total:
              </Typography>
            </li>

            {/* TENDER: Planned Effort comes FIRST */}
            {projectType === "TENDER" && financePermission && (
              <li className="text-right mt-0 mb-auto w-150px d-flex flex-column justify-content-center" data-cy="planned-effort">
                <Typography variant="body1">
                  {forecastHoursDisplay || "0 hrs"}
                </Typography>
              </li>
            )}

            {financePermission && (
              <>
                <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center" data-cy="material">
                  <Typography variant="body1" data-cy="sale-amount">{materialSaleAmount}</Typography>
                  <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                    {materialProfit} ({materialProfitPercentage})
                  </Typography>
                </li>
                <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center" data-cy="contractor">
                  <Typography variant="body1" data-cy="sale-amount">{subcontractorSaleAmount}</Typography>
                  <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                    {subcontractorProfit} ({subcontractorProfitPercentage})
                  </Typography>
                </li>
                <li className="text-right mt-0 w-150px d-flex flex-column justify-content-center" data-cy="resource">
                  <Typography variant="body1" data-cy="sale-amount">{laborSaleAmount}</Typography>
                  <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                    {laborProfit} ({laborProfitPercentage})
                  </Typography>
                </li>
              </>
            )}

            {/* Non-TENDER: Effort comes AFTER Material/Contractor/Resource */}
            {projectType !== "TENDER" && (
              <li className="text-end mt-0 w-150px d-flex flex-column justify-content-center">
                <Typography variant="body1" data-cy="project-variation-forecast-hours">
                  {forecastHoursDisplay || "0 hrs"}
                </Typography>
                {!isSectionLocked ? (
                  <Typography className="fst-12" variant="subtitle1" color="textSecondary">
                    {actualHoursDisplay || "0 hrs"} (<span>{effortCompletionPercentage}</span>)
                  </Typography>
                ) : (
                  <Typography className="text-muted" variant="body1">-</Typography>
                )}
              </li>
            )}

            {projectType !== "TENDER" && (
              <li className="text-end mt-0 w-150px d-flex flex-column justify-content-center">
                {isSectionLocked ? <span className="text-muted">-</span> : completionPercentage}
              </li>
            )}

            {financePermission && (
              <li className="text-right mt-0 w-180px d-flex flex-column justify-content-center" data-cy="total">
                <Typography variant="body1" className="option-top-text text-right" data-cy="sale-amount">{totalSaleAmount}</Typography>
                <Typography className="fst-12 text-right" variant="subtitle1" color="textSecondary">
                  {totalProfit} ({totalProfitPercentage})
                </Typography>
              </li>
            )}
            <li className="ms-2 w-180px">
              <Typography variant="body1"></Typography>
            </li>
          </ul>
        </>
      )}
    </>
  );
}

export default tasks;

function ProfitInPercentage({
  projectType,
  financePermission,
  stageTask,
  taskCost,
  isSectionLocked,
}) {
  const {
    materialSaleAmount,
    laborSaleAmount,
    subcontractorSaleAmount,
    totalSaleAmount,
    materialProfit,
    laborProfit,
    subcontractorProfit,
    totalProfit,
    materialProfitPercentage,
    laborProfitPercentage,
    subcontractorProfitPercentage,
    totalProfitPercentage,
    completionPercentage,
  } = getFormatedData(
    idx(taskCost, (_) => _[stageTask.id || "projectlevel"]) || [],
  );
  return (
    <>
      {/* TENDER: Planned Effort comes FIRST (before Material/Contractor/Resource) to match section header */}
      {projectType === "TENDER" && financePermission && (
        <li className="text-right mt-0 mb-auto w-150px d-flex flex-column justify-content-center" data-cy="planned-effort">
          <Typography variant="body2" color="textSecondary">
            {stageTask.forecastHoursDisplay || "0 hrs"}
          </Typography>
        </li>
      )}

      {financePermission && (
        <>
          <li className="text-right w-150px d-flex flex-column justify-content-center" data-cy="material">
            <span data-cy="sale-amount">{materialSaleAmount}</span>
            <Typography className="fst-12" variant="subtitle1" color="textSecondary">
              {materialProfit} ({materialProfitPercentage})
            </Typography>
          </li>
          <li className="text-right w-150px d-flex flex-column justify-content-center" data-cy="contractor">
            <span data-cy="sale-amount">{subcontractorSaleAmount}</span>
            <Typography className="fst-12" variant="subtitle1" color="textSecondary">
              {subcontractorProfit} ({subcontractorProfitPercentage})
            </Typography>
          </li>
          <li className="text-right w-150px d-flex flex-column justify-content-center" data-cy="resource">
            <span data-cy="sale-amount">{laborSaleAmount}</span>
            <Typography className="fst-12" variant="subtitle1" color="textSecondary">
              {laborProfit} ({laborProfitPercentage})
            </Typography>
          </li>
        </>
      )}

      {/* Non-TENDER: Effort comes AFTER Material/Contractor/Resource */}
      {projectType != "TENDER" && (
        <li className="text-right w-150px d-flex flex-column justify-content-center">
          <Typography variant="body2" className="fw-bold" data-cy="task-forecast-hours">
            {stageTask.forecastHoursDisplay || "0 hrs"}
          </Typography>
          {!isSectionLocked ? (
            <Typography variant="caption" color="textSecondary" className="fst-12">
              {stageTask.actualHoursDisplay || "0 hrs"} ({stageTask.effortCompletionPercentage})
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary">-</Typography>
          )}
        </li>
      )}

      {projectType != "TENDER" && (
        <li className="text-end w-150px d-flex flex-column justify-content-center">
          {isSectionLocked ? (
            <Typography variant="body2" color="textSecondary">-</Typography>
          ) : (
            <Typography variant="body2" color="textSecondary">
              {completionPercentage}
            </Typography>
          )}
        </li>
      )}

      {financePermission && (
        <li className="text-right w-180px d-flex flex-column justify-content-center" data-cy="total">
          <span data-cy="sale-amount">{totalSaleAmount}</span>
          <Typography className="fst-12" variant="subtitle1" color="textSecondary">
            {totalProfit} ({totalProfitPercentage})
          </Typography>
        </li>
      )}
    </>
  );
}

function Profit({
  stageTask,
  financePermission,
  projectType,
  updatePermission,
  quoteType,
  type,
  deleteDialogHandler,
  isSectionLocked,
}) {
  return (
    <>
      {stageTask?.subTasks?.map((subTask, subtaskindex) => {
        const descriptionText =
          subTask.description.slice(0, 20) +
          (subTask.description.length > 20 ? "..." : "");

        const materialSale = idx(subTask, (_) => _.materialTotal_SaleAmount) || 0;
        const laborSale = idx(subTask, (_) => _.laborTotal_SaleAmount) || 0;
        const subcontractorSale = idx(subTask, (_) => _.subcontractorTotal_SaleAmount) || 0;
        const totalSale = roundValue(
          parseFloat(materialSale) + parseFloat(laborSale) + parseFloat(subcontractorSale), 2
        );

        return (
          <ul key={subtaskindex} className="tabel-list">
            <li className="sub-content-first-cell w-10">
              <img src={subtaskLine} alt="subtask" /> {descriptionText}
            </li>

            {/* TENDER: Planned Effort comes FIRST */}
            {projectType === "TENDER" && financePermission && (
              <li className="text-right mt-0 mb-auto w-150px d-flex flex-column justify-content-center" data-cy="planned-effort">
                {subTask.forecastHoursDisplay || "0 hrs"}
              </li>
            )}

            {/* Material / Contractor / Resource columns */}
            {financePermission && (
              <>
                <li className="text-right w-150px d-flex flex-column justify-content-center" data-cy="material">
                  <span>{numberFormat(materialSale)}</span>
                </li>
                <li className="text-right w-150px d-flex flex-column justify-content-center" data-cy="contractor">
                  <span>{numberFormat(subcontractorSale)}</span>
                </li>
                <li className="text-right w-150px d-flex flex-column justify-content-center" data-cy="resource">
                  <span>{numberFormat(laborSale)}</span>
                </li>
              </>
            )}

            {/* Effort column (non-TENDER) */}
            {projectType !== "TENDER" && (
              <li className="text-end w-150px d-flex flex-column justify-content-center">
                <div>{subTask.forecastHoursDisplay || "0 hrs"}</div>
              </li>
            )}

            {/* Completion % (non-TENDER) */}
            {projectType !== "TENDER" && (
              <li className="text-end w-150px d-flex flex-column justify-content-center">
                {isSectionLocked ? (
                  <span className="text-muted">-</span>
                ) : (
                  `${roundValue(idx(subTask, (_) => _.percentageComplete) || 0, 0)}%`
                )}
              </li>
            )}

            {/* Total (Sale & Profit) */}
            {financePermission && (
              <li className="text-right w-180px d-flex flex-column justify-content-center" data-cy="total">
                <span>{numberFormat(totalSale)}</span>
              </li>
            )}

            {/* Action icons */}
            <li className="action-icons-task pe-3 gap-1 d-flex justify-content-end m-0 w-5">
              {updatePermission &&
                ((quoteType == "quote" && projectType == "TENDER") ||
                  quoteType == "noquote") && (
                  <img
                    src={deleteIcon}
                    alt="edit"
                    onClick={() => deleteDialogHandler(subTask.id, type)}
                  />
                )}
            </li>
          </ul>
        );
      })}
    </>
  );
}
